import moment from 'moment';
import { isEqual } from 'lodash';
import { useMemo, useState, useEffect } from 'react';
import { STTimeLineChartData, TimeFrame } from '../api/monitoring-ui.model';
import { useAdaptableParams } from '../../../util/hooks/useAdaptableParams';
import { getLevelOfView } from '../../groups/selectors/getLevelOfView';
import { useAppDispatch, useAppSelector } from '../../store.model';
import { useDevicesControlUnits } from '../../controlUnit/hooks/useDevicesControlUnits';
import { useCUsTranslate } from '../../CUTranslation/hooks/useCUsTranslate';
import useChannelsFromComposableDevice from '../../composableDevice/hooks/useChannelsFromComposableDevice';
import {
  CommanderMonitoringFilterLogicalObject,
  getFilterLogicalObjectFromZipDeviceControlUnits,
} from '../api/util/filter/filter.model';
import usePreferredTemperatureUnit from '../../settings/hooks/usePreferredTemperatureUnit';
import { updateFilter } from '../thunk';
import useLabStateFilters from '../../../components/DeviceManager/Device/Monitoring/components/Lab/useLabStateFilters';
import useMultipleQueries from '../../../util/rtk/useQueries';
import MonitoringAPI from '../api/MonitoringAPI';
import { getSensorInformation } from './useSelectors';
import { filterAnalogChannels } from '../api/util/filter/filterAnalogChannels';
import { filterExtrapolatedDigitalChannelData } from '../api/util/filter/filterExtrapolatedDigitalChannelData';
import { filterDigitalChannels } from '../api/util/filter/filterDigitalChannels';
import { TIME_FRAME_TYPES, TIME_RANGE_TYPES } from 'stoerk-ui-components';
import { CHANNEL_TYPE } from '../../reports/api/Report.model';
import { getFilterStored } from '../selectors';
import { checkDefaultChannels } from '../api/util/filter/checkDefaultChannels';
import { areFiltersUpdated } from '../../groups/selectors/areFiltersUpdated';
import { useControlUnitsInComposableDevice } from './useControlUnitsInComposableDevice';
import { filtersAreEmpty } from '../../groups/selectors/filtersAreEmpty';

interface UseMetricsComposableArgs {
  defaultTimeFrame?: TimeFrame;
  showAllMetrics?: boolean;
  composableDeviceId: string;
  defaultAnalogFilter?: CommanderMonitoringFilterLogicalObject;
  defaultDigitalFilter?: CommanderMonitoringFilterLogicalObject;
}

const getDefaultTimeFrame = (): TimeFrame => {
  const initialMemoizedTimeFrame = useMemo(() => {
    return {
      start: moment().subtract(6, 'hours'),
      end: moment(),
      timeFrameType: TIME_FRAME_TYPES.LAST_6_HOURS,
      timeRangeType: TIME_RANGE_TYPES.QUICK,
    };
  }, []);

  return initialMemoizedTimeFrame;
};
export const useMetricsComposable = ({
  defaultTimeFrame = getDefaultTimeFrame(),
  showAllMetrics = false,
  composableDeviceId,
  defaultAnalogFilter,
  defaultDigitalFilter,
}: UseMetricsComposableArgs) => {
  const params = useAdaptableParams<{
    deviceId?: string;
    groupId?: string;
    controlUnitId?: string;
  }>();

  const showLevelView = getLevelOfView(params);
  const dispatch = useAppDispatch();

  const [_, loadingDevicesControlUnits] = useDevicesControlUnits();
  const [, loadingCUsTranslate] = useCUsTranslate();
  const [readyToFetch, setReadyToFetch] = useState(false);

  const enhancedDevicesControlUnits =
    useControlUnitsInComposableDevice(composableDeviceId);
  // Get composable device channels
  const { data: composableChannels, isLoading: loadingComposableChannels } =
    useChannelsFromComposableDevice(composableDeviceId);

  // Filter controls
  const [timeFrame, setTimeFrame] = useState<TimeFrame>(defaultTimeFrame);

  const defaultFilterObjectEventsStored = useAppSelector(
    (store) => getFilterStored(store, { ...params, type: 'events' }),
    isEqual
  );

  const defaultFilterObjectChannelsStored = useAppSelector(
    (store) => getFilterStored(store, { ...params, type: 'channels' }),
    isEqual
  );
  const defaultFilterObjectChannels = useMemo(() => {
    if (defaultAnalogFilter) return defaultAnalogFilter;

    const areFiltersEmpty =
      defaultFilterObjectChannelsStored &&
      filtersAreEmpty(defaultFilterObjectChannelsStored);
    // Hydrate stored value if not exists or has all the CUs
    return !showAllMetrics &&
      defaultFilterObjectChannelsStored &&
      areFiltersUpdated(
        enhancedDevicesControlUnits,
        defaultFilterObjectChannelsStored,
        'channels'
      ) &&
      !areFiltersEmpty
      ? defaultFilterObjectChannelsStored
      : // Get default
        checkDefaultChannels(
          getFilterLogicalObjectFromZipDeviceControlUnits(
            enhancedDevicesControlUnits,
            'channels',
            false
          ),
          showAllMetrics
        );
  }, [enhancedDevicesControlUnits]);

  const defaultFilterObjectEvents = useMemo(() => {
    if (defaultDigitalFilter) return defaultDigitalFilter;
    // Hydrate stored value if not exists or has all the CUs
    const areFiltersEmpty =
      defaultFilterObjectEventsStored &&
      filtersAreEmpty(defaultFilterObjectEventsStored);

    return !showAllMetrics &&
      defaultFilterObjectEventsStored &&
      areFiltersUpdated(
        enhancedDevicesControlUnits,
        defaultFilterObjectEventsStored,
        'events'
      ) &&
      !areFiltersEmpty
      ? defaultFilterObjectEventsStored
      : // Get default
        checkDefaultChannels(
          getFilterLogicalObjectFromZipDeviceControlUnits(
            enhancedDevicesControlUnits,
            'events',
            false
          ),
          showAllMetrics
        );
  }, [enhancedDevicesControlUnits]);

  // Create filter objects based only on composable device channels
  const composableFilterObject = useMemo(() => {
    if (!composableChannels || loadingComposableChannels) {
      return {};
    }
    const filterObject: CommanderMonitoringFilterLogicalObject = {};

    // Group channels by device and control unit
    composableChannels.forEach((channel) => {
      const { deviceID, cuID, channelID } = channel;

      if (!filterObject[deviceID]) {
        filterObject[deviceID] = {};
      }
      if (!filterObject[deviceID][cuID]) {
        filterObject[deviceID][cuID] = {};
      }

      const savedChannel =
        defaultFilterObjectEvents[deviceID]?.[cuID]?.[channelID] ??
        defaultFilterObjectChannels[deviceID]?.[cuID]?.[channelID];

      filterObject[deviceID][cuID][channelID] = savedChannel ?? false;
    });

    return filterObject;
  }, [
    composableChannels,
    loadingComposableChannels,
    defaultFilterObjectEvents,
    defaultFilterObjectChannels,
  ]);

  // Separate analog and digital channels
  const { analogFilterObject, digitalFilterObject } = useMemo(() => {
    if (!composableChannels) {
      return { analogFilterObject: {}, digitalFilterObject: {} };
    }

    const analog: CommanderMonitoringFilterLogicalObject = {};
    const digital: CommanderMonitoringFilterLogicalObject = {};

    composableChannels.forEach((channel) => {
      const { deviceID, cuID, channelID, type } = channel;

      const targetFilter = type === CHANNEL_TYPE.ANALOG ? analog : digital;
      if (!targetFilter[deviceID]) {
        targetFilter[deviceID] = {};
      }
      if (!targetFilter[deviceID][cuID]) {
        targetFilter[deviceID][cuID] = {};
      }

      const savedChannel =
        composableFilterObject[deviceID]?.[cuID]?.[channelID];

      targetFilter[deviceID][cuID][channelID] = savedChannel ?? false;
    });

    return { analogFilterObject: analog, digitalFilterObject: digital };
  }, [
    composableChannels,
    defaultFilterObjectChannels,
    defaultFilterObjectEvents,
  ]);

  const devicesIds = useMemo(() => {
    return Array.from(
      new Set<string>(
        (composableChannels || []).map((channel) => channel.deviceID)
      )
    );
  }, [composableChannels]);

  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();

  const digitalControls =
    useState<CommanderMonitoringFilterLogicalObject>(digitalFilterObject);
  const analogControls =
    useState<CommanderMonitoringFilterLogicalObject>(analogFilterObject);

  const [digitalFilterObjectEvents, setDigitalFilterObject] = digitalControls;
  const [analogFilterObjectEvents, setAnalogFilterObject] = analogControls;

  const externalSetDigitalFilterObject: typeof setDigitalFilterObject = (
    value
  ) => {
    setDigitalFilterObject(value);
    dispatch(
      updateFilter({
        ...params,
        type: 'events',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };

  const externalSetAnalogFilterObject: typeof setAnalogFilterObject = (
    value
  ) => {
    setAnalogFilterObject(value);
    dispatch(
      updateFilter({
        ...params,
        type: 'channels',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };

  const labStateFilters = useLabStateFilters(enhancedDevicesControlUnits);
  const [connectionDigitalFilterObject] =
    labStateFilters.connectionDigitalFilterControls;
  const [cloudGeneratedFilterLogicalObject] =
    labStateFilters.cloudGeneratedFilterControls;

  const labBandwidthFeatureArgs = useMemo(() => {
    return devicesIds.map((deviceId) => ({
      deviceId,
      start: timeFrame.start.valueOf(),
      end: timeFrame.end.valueOf(),
      commanderFilterObject: cloudGeneratedFilterLogicalObject[deviceId] || {},
      includeCloudGenerated: true,
    }));
  }, [
    cloudGeneratedFilterLogicalObject,
    timeFrame.start,
    timeFrame.end,
    devicesIds,
  ]);

  const connectionArgs = useMemo(() => {
    return devicesIds
      .map((deviceId) => {
        const channels = Object.values(
          connectionDigitalFilterObject[deviceId] || {}
        ).flatMap((controlUnitObject = {}) =>
          Object.entries(controlUnitObject)
            .filter(([_key, value]) => !!value)
            .map(([key, _value]) => key)
        );
        return {
          channels,
          deviceId,
          start: moment(timeFrame.start).add(-1, 'day').valueOf(),
          end: moment(timeFrame.end).add(1, 'day').valueOf(),
        };
      })
      .filter((arg) => !!arg.channels.length);
  }, [
    devicesIds,
    connectionDigitalFilterObject,
    timeFrame.start,
    timeFrame.end,
  ]);

  const eventsArgs = useMemo(() => {
    return devicesIds.map((deviceId) => ({
      deviceId,
      start: moment(timeFrame.start).add(-1, 'day').valueOf(),
      end: moment(timeFrame.end).add(1, 'day').valueOf(),
      commanderFilterObject: digitalFilterObjectEvents[deviceId] || {},
    }));
  }, [devicesIds, digitalFilterObjectEvents, timeFrame.start, timeFrame.end]);

  const sensorArgs = useMemo(() => {
    return devicesIds.map((deviceId) => ({
      deviceId,
      start: timeFrame.start.valueOf(),
      end: timeFrame.end.valueOf(),
      commanderFilterObject: analogFilterObjectEvents[deviceId] || {},
      includeCloudGenerated: false,
    }));
  }, [devicesIds, analogFilterObjectEvents, timeFrame.start, timeFrame.end]);

  const {
    isLoading: isLoadingConnection,
    isError: errorConnectionInfo,
    isFetching: fetchingConnectionInfo,
  } = useMultipleQueries(
    MonitoringAPI.endpoints.getConnectionInfoByID,
    connectionArgs,
    {
      skip: labStateFilters.labFiltersFeatures.labConnectionFeature === false,
    }
  );

  const {
    isLoading: loadingBandwidthSensorData,
    isFetching: fetchingBandwidthSensorData,
    error: errorBandwidthSensorData,
  } = useMultipleQueries(
    MonitoringAPI.endpoints.getSensordataByDeviceID,
    labBandwidthFeatureArgs,
    {
      skip: labStateFilters.labFiltersFeatures.labBandwidthFeature === false,
    }
  );

  const {
    isLoading: loadingEvents,
    isFetching: fetchingEvents,
    error: errorEvents,
  } = useMultipleQueries(MonitoringAPI.endpoints.getEventsByID, eventsArgs);

  const {
    isLoading: loadingSensorData,
    isFetching: fetchingSensorData,
    error: errorSensorData,
  } = useMultipleQueries(
    MonitoringAPI.endpoints.getSensordataByDeviceID,
    sensorArgs
  );

  const loading =
    !readyToFetch ||
    loadingEvents ||
    loadingSensorData ||
    fetchingEvents ||
    fetchingSensorData ||
    isLoadingConnection ||
    fetchingConnectionInfo ||
    loadingDevicesControlUnits ||
    loadingBandwidthSensorData ||
    fetchingBandwidthSensorData ||
    loadingComposableChannels;

  const error =
    errorEvents ||
    errorSensorData ||
    errorConnectionInfo ||
    errorBandwidthSensorData;

  const { sensorData, digitalData } = useAppSelector((state) =>
    getSensorInformation(state, {
      devicesControlUnits: enhancedDevicesControlUnits,
      digitalFilterObjectEvents,
      analogFilterObjectEvents,
      start: timeFrame.start.valueOf(),
      end: timeFrame.end.valueOf(),
      showLevelOption: showLevelView,
      preferredTemperatureUnit: preferredTemperatureUnit,
      labStateFilters: labStateFilters,
    })
  );

  const digitalChannels = useMemo(() => {
    return devicesIds.reduce(
      (acc, deviceId) => ({
        ...acc,
        ...digitalData[deviceId],
      }),
      {} as STTimeLineChartData
    );
  }, [devicesIds, digitalData]);

  const sensordataFiltered = useMemo(() => {
    if (loading) return [];

    return filterAnalogChannels(
      sensorData,
      analogFilterObjectEvents,
      cloudGeneratedFilterLogicalObject
    );
  }, [
    sensorData,
    analogFilterObjectEvents,
    cloudGeneratedFilterLogicalObject,
    loading,
  ]);

  const digitalChannelsFiltered = useMemo(() => {
    if (loading) return {} as STTimeLineChartData;
    return filterExtrapolatedDigitalChannelData(
      filterDigitalChannels(
        digitalChannels,
        digitalFilterObjectEvents,
        connectionDigitalFilterObject
      ),
      timeFrame.start.unix(),
      timeFrame.end.unix()
    );
  }, [
    digitalChannels,
    digitalFilterObjectEvents,
    connectionDigitalFilterObject,
    loading,
    timeFrame.end,
    timeFrame.start,
  ]);

  // Map sensor data with composable device names
  const sensordataWithComposableLabels = useMemo(() => {
    if (!composableChannels || !sensordataFiltered.length)
      return sensordataFiltered;

    return sensordataFiltered.map((item) => {
      const matchingChannel = composableChannels.find(
        (channel) =>
          channel.deviceID === item.deviceId &&
          channel.cuID === item.controlunit &&
          channel.channelID === item.channel
      );

      if (matchingChannel?.name) {
        return {
          ...item,
          name: matchingChannel.name,
          nameOriginal: item.name,
        };
      }

      return item;
    });
  }, [sensordataFiltered, composableChannels]);

  // Map digital channels with composable device names
  const digitalChannelsWithComposableLabels = useMemo(() => {
    if (!composableChannels || !Object.keys(digitalChannelsFiltered).length) {
      return digitalChannelsFiltered;
    }

    const mappedChannels: STTimeLineChartData = {};

    Object.entries(digitalChannelsFiltered).forEach(([channelKey, data]) => {
      const matchingChannel = composableChannels.find((channel) => {
        return (
          channelKey ===
          `${channel.deviceID}-${channel.cuID}-${channel.channelID}`
        );
      });

      const newChannelData = {
        ...data,
        name: matchingChannel?.name || data.name,
        nameOriginal: matchingChannel?.name || data.name,
        textTranslation: matchingChannel?.name || data.textTranslation,
      };

      mappedChannels[channelKey] = newChannelData;
    });

    return mappedChannels;
  }, [digitalChannelsFiltered, composableChannels]);

  // Initialize filters when composable channels are loaded
  useEffect(() => {
    if (
      !(
        loadingDevicesControlUnits ||
        loadingCUsTranslate ||
        loadingComposableChannels
      ) &&
      readyToFetch !== true &&
      Object.keys(composableFilterObject).length > 0
    ) {
      setDigitalFilterObject(digitalFilterObject);
      setAnalogFilterObject(analogFilterObject);
      setReadyToFetch(true);
    }
  }, [
    analogFilterObject,
    digitalFilterObject,
    composableFilterObject,
    loadingCUsTranslate,
    loadingDevicesControlUnits,
    loadingComposableChannels,
    readyToFetch,
    setAnalogFilterObject,
    setDigitalFilterObject,
  ]);

  return {
    timeControls: { timeFrame, setTimeFrame },
    digitalControls: [
      digitalFilterObjectEvents,
      externalSetDigitalFilterObject,
    ] as typeof digitalControls,
    analogControls: [
      analogFilterObjectEvents,
      externalSetAnalogFilterObject,
    ] as typeof analogControls,
    labStateFilters,
    metrics: {
      sensordata: sensordataWithComposableLabels,
      digitalChannels: digitalChannelsWithComposableLabels,
    },
    loading: loading && enhancedDevicesControlUnits.length > 0,
    error,
    composableChannels,
  };
};
